import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Slider from "react-slick";
import {
  HiOutlineHome,
  HiOutlineRocketLaunch,
  HiOutlineCheckBadge,
  HiOutlineChevronLeft,
  HiOutlineInformationCircle,
  HiOutlineClock,
  HiOutlineArrowUpRight,
} from "react-icons/hi2";
import { appStoreService } from "../services";
import { MAX_APPS_SEED } from "../data/maxAppsDefinitions";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./AppDetailPage.css";

/**
 * Detailed information about a single MaX App. 
 * Shows features, screenshots, release notes, and related apps.
 */
function AppDetailPage() {
  const { appCode } = useParams();
  const navigate = useNavigate();
  
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allApps, setAllApps] = useState([]);

  useEffect(() => {
    async function loadAppDetail() {
      try {
        setLoading(true);
        // Fetch specific app
        const found = await appStoreService.getAppByCode(appCode);
        
        // Fetch all apps for "Related Apps" sidebar
        const storeApps = await appStoreService.getPublicApps();
        const appsList = (storeApps && storeApps.length > 0) ? storeApps : MAX_APPS_SEED;
        setAllApps(appsList);

        if (found) {
          setApp(found);
        } else {
          // Fallback to seeds if not in API yet
          const seedFound = MAX_APPS_SEED.find(a => a.code === appCode);
          setApp(seedFound);
        }
      } catch (err) {
        console.error("Failed to fetch app detail:", err);
        setApp(MAX_APPS_SEED.find(a => a.code === appCode));
        setAllApps(MAX_APPS_SEED);
      } finally {
        setLoading(false);
      }
    }
    loadAppDetail();
    // Scroll to top when switching apps
    window.scrollTo(0, 0);
  }, [appCode]);

  const relatedApps = useMemo(() => {
    if (!app) return [];
    return allApps
      .filter(a => a.code !== app.code && a.category === app.category)
      .slice(0, 4);
  }, [app, allApps]);

  const featuresList = useMemo(() => {
    // Priority: API DTO 'features' array > Legacy/Seed 'features' > JSON parsing of 'featuresJson'
    if (Array.isArray(app?.features) && app.features.length > 0) return app.features;
    if (app?.featuresJson) {
      try {
        const parsed = typeof app.featuresJson === 'string' ? JSON.parse(app.featuresJson) : app.featuresJson;
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        console.warn("Failed to parse featuresJson", e);
      }
    }
    return app?.features || [];
  }, [app]);

  const screenshotsList = useMemo(() => {
    // Priority: API DTO 'screenshots' array > JSON parsing of 'screenshotsJson'
    if (Array.isArray(app?.screenshots) && app.screenshots.length > 0) return app.screenshots;
    if (app?.screenshotsJson) {
      try {
        const parsed = typeof app.screenshotsJson === 'string' ? JSON.parse(app.screenshotsJson) : app.screenshotsJson;
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        console.warn("Failed to parse screenshotsJson", e);
      }
    }
    return [];
  }, [app]);

  if (loading) return <div className="app-detail-loading">Loading application details...</div>;
  if (!app) return <div className="app-detail-notfound">Application "{appCode}" not found.</div>;

  const sliderSettings = {
    dots: true,
    infinite: screenshotsList.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
  };

  return (
    <div className="app-detail-container" style={{ '--app-color': app.colorHex || '#4F46E5' }}>
      <nav className="app-detail-breadcrumb">
        <Link to="/home" className="breadcrumb-item"><HiOutlineHome /> Home</Link>
        <span className="breadcrumb-sep">/</span>
        <Link to="/home/apps" className="breadcrumb-item">Apps</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">{app.name}</span>
      </nav>

      <button className="btn-back" onClick={() => navigate("/home/apps")}>
        <HiOutlineChevronLeft /> Back to Store
      </button>

      <div className="app-detail-layout">
        <div className="app-detail-main">
          {/* --- Hero Section --- */}
          <section className="app-hero">
            <div className="app-hero-bg" style={{ backgroundColor: `${app.colorHex}22` }} />
            <div className="app-hero-content">
              <div className="app-hero-icon" style={{ backgroundColor: app.colorHex }}>
                <span>{app.iconEmoji}</span>
              </div>
              <div className="app-hero-info">
                <div className="app-hero-header">
                  <h1 className="app-name">{app.name}</h1>
                  <div className="app-badges">
                    <span className="badge category">{app.category}</span>
                    <span className="badge version">v{app.version || '1.0.0'}</span>
                  </div>
                </div>
                <p className="app-long-desc">{app.description}</p>
                <div className="app-actions">
                  <button className="btn-launch" onClick={() => navigate(app.primaryPath)}>
                    <HiOutlineRocketLaunch /> Open Application
                  </button>
                  <span className="app-publisher">By MaX Platform Team</span>
                </div>
              </div>
            </div>
          </section>

          {/* --- Screenshots --- */}
          {screenshotsList.length > 0 && (
            <section className="app-detail-section">
              <h2 className="section-title"><HiOutlineInformationCircle /> App Previews</h2>
              <div className="app-screenshots-carousel">
                <Slider {...sliderSettings}>
                  {screenshotsList.map((url, idx) => (
                    <div key={idx} className="screenshot-slide">
                      <img src={url} alt={`${app.name} screenshot ${idx + 1}`} />
                    </div>
                  ))}
                </Slider>
              </div>
            </section>
          )}

          {/* --- Features --- */}
          <section className="app-detail-section">
            <h2 className="section-title"><HiOutlineInformationCircle /> Core Modules & Features</h2>
            <div className="features-grid">
              {featuresList.map((feature, idx) => (
                <div key={idx} className="feature-item">
                  <HiOutlineCheckBadge className="feature-icon" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </section>

          {/* --- Release Notes --- */}
          {app.releaseNotes && (
            <section className="app-detail-section">
              <h2 className="section-title"><HiOutlineClock /> Recent Updates</h2>
              <div className="release-notes-box">
                <pre>{app.releaseNotes}</pre>
              </div>
            </section>
          )}
        </div>

        {/* --- Sidebar --- */}
        <aside className="app-detail-sidebar">
          <div className="sidebar-section">
            <h3 className="sidebar-title">Information</h3>
            <div className="info-list">
              <div className="info-row">
                <span className="label">Category</span>
                <span className="value">{app.category}</span>
              </div>
              <div className="info-row">
                <span className="label">Last Updated</span>
                <span className="value">Apr 05, 2026</span>
              </div>
              <div className="info-row">
                <span className="label">License</span>
                <span className="value">Enterprise</span>
              </div>
            </div>
          </div>

          <div className="sidebar-section">
            <h3 className="sidebar-title">Related Apps</h3>
            <div className="mini-apps-list">
              {relatedApps.length > 0 ? (
                relatedApps.map(ra => (
                  <Link key={ra.code} to={`/home/apps/detail/${ra.code}`} className="mini-app-item">
                    <div className="mini-app-icon" style={{ backgroundColor: `${ra.colorHex}22`, color: ra.colorHex }}>
                      {ra.iconEmoji}
                    </div>
                    <div className="mini-app-info">
                      <span className="name">{ra.name}</span>
                      <span className="cat">{ra.category}</span>
                    </div>
                    <HiOutlineArrowUpRight className="arrow" />
                  </Link>
                ))
              ) : (
                <p className="no-related">No other apps in this category yet.</p>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default AppDetailPage;
